import { IWorkNote } from "../../../../../../types/types";

/** Notes oldest first, like a chat (newest at the bottom). */
export const notesInChatOrder = (notes: IWorkNote[] | undefined) =>
  [...(notes ?? [])].sort(
    (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
  );

export const sameDay = (a: Date | string, b: Date | string) =>
  new Date(a).toDateString() === new Date(b).toDateString();

/** True when a note was changed after it was posted (more than a minute later). */
export const wasEdited = (note: Pick<IWorkNote, "createdAt" | "updatedAt">) =>
  !!note.updatedAt &&
  new Date(note.updatedAt).getTime() - new Date(note.createdAt).getTime() >
    60_000;

// A file shared in the chat is a note "📎 name" with a hidden link to the
// work file (an HTML comment, which the note renderer leaves out).
const FILE_MARK = /<!--file:([\w-]+)-->/;

export const fileNoteText = (fileName: string, fileId: string) =>
  `📎 ${fileName}<!--file:${fileId}-->`;

export const parseFileNote = (text: string | null | undefined) => {
  const match = text?.match(FILE_MARK);
  if (!text || !match) return null;
  return {
    id: match[1],
    name: text
      .replace(FILE_MARK, "")
      .replace(/^📎\s*/u, "")
      .trim(),
  };
};

export const fileTypeLabel = (fileName: string) => {
  const dot = fileName.lastIndexOf(".");
  return dot > 0
    ? fileName
        .slice(dot + 1)
        .toUpperCase()
        .slice(0, 4)
    : "FILE";
};

/** The file an upload just added: newest one with that name not seen before. */
export const findUploadedFile = <
  T extends { id: string; originalFileName: string; createdDate: string },
>(
  before: string[],
  after: T[],
  fileName: string,
) =>
  after
    .filter((file) => !before.includes(file.id))
    .sort(
      (a, b) =>
        Number(b.originalFileName === fileName) -
          Number(a.originalFileName === fileName) ||
        new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime(),
    )[0];
